import { Compass } from "@phosphor-icons/react";

import { FavoriteLead } from "features/leads/components";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "common/components/ui/Popover";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import useLead from "../../../../leads/datahooks/useLead";
import { useActiveConversationContext } from "../../../context/ActiveConversationContext";
import { useNewConversation } from "../../../hooks/useNewConversation";
import useSNConversation from "../../../hooks/useSNConversation";
import {
  getGroupParticipantsString,
  getParticipantJobInfo,
} from "../../../utils";
import GroupAvatar from "../../GroupAvatar/GroupAvatar";
import GroupParticipantItem from "./GroupParticipantItem";

import type { JSX } from "react";

interface ParticipantInfoProps {
  onParticipantSelect: (participantId: string) => void;
}

export default function ParticipantInfo({
  onParticipantSelect,
}: ParticipantInfoProps) {
  const { activeConversation } = useActiveConversationContext();
  const { newConversation } = useNewConversation();
  const { permissions } = useSelectedWorkspaceContext();
  const { isSNConversation } = useSNConversation(activeConversation);

  const participants = activeConversation
    ? activeConversation.participants
    : [newConversation.participant];

  const isGroup = participants.length > 1;

  const { picture_url: pictureUrl, full_name: fullName, id } = participants[0];

  const { lead } = useLead({
    profileId: isGroup ? undefined : participants[0].id,
  });

  let avatarComponent: JSX.Element;
  let participantsLabelComponent = (
    <span className="line-clamp-1 text-sm-bold lg:text-md-bold text-left">
      {isGroup ? getGroupParticipantsString(participants) : fullName}
    </span>
  );
  if (isGroup) {
    avatarComponent = <GroupAvatar participants={participants} size="md" />;
    participantsLabelComponent = (
      <Popover>
        <PopoverTrigger
          asChild
          className="data-[state=open]:underline cursor-pointer hover:underline"
        >
          {participantsLabelComponent}
        </PopoverTrigger>
        <PopoverContent className="max-w-64">
          {/* Popover header */}
          <div className="border-b border-black-200 p-2 text-sm text-black-500">
            {participants.length} members
          </div>

          {/* Popover body */}
          {participants.map((participant) => {
            return (
              <GroupParticipantItem
                key={participant.id}
                participant={participant}
                onSelect={() => onParticipantSelect(participant.id)}
              />
            );
          })}
        </PopoverContent>
      </Popover>
    );
  } else {
    avatarComponent = <ProfileImage src={pictureUrl} />;
    if (permissions.includes("leads_full")) {
      avatarComponent = (
        <button
          onClick={() => onParticipantSelect(id)}
          type="button"
          className="shrink-0 cursor-pointer"
        >
          {avatarComponent}
        </button>
      );
      participantsLabelComponent = (
        <button
          type="button"
          className="cursor-pointer hover:underline"
          onClick={() => onParticipantSelect(id)}
        >
          {participantsLabelComponent}
        </button>
      );
    }
  }

  let subtitleComponent = <Skeleton className="h-4 w-52" />;

  if (isGroup || lead) {
    subtitleComponent = (
      <span className="line-clamp-1 text-xs text-black-600">
        {isGroup
          ? `${participants.length} members`
          : getParticipantJobInfo(lead)}
      </span>
    );
  }

  return (
    <div className="flex flex-row items-center gap-1.5">
      {avatarComponent}
      <div className="flex flex-col items-start justify-center text-start">
        <div className="flex flex-col lg:gap-y-0.5">
          <div className="flex flex-row items-center gap-x-1">
            {participantsLabelComponent}
            {isSNConversation && (
              <Compass weight="fill" className="size-4 text-blue-500" />
            )}
            {!isGroup && (
              <FavoriteLead
                leadId={participants[0].id}
                isLead={participants[0].is_lead}
                isFavorite={participants[0].favorite}
              />
            )}
          </div>
          {subtitleComponent}
        </div>
      </div>
    </div>
  );
}
